// AGC: TeamUsers - Team member management page
// AGC: Visual Refresh Design System - matches Tasks/Lots/Weather pages
// AGC: Shows list of team members with occupancy indicators
// AGC: Includes current user (account owner) in the list

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useFarms } from '../../context'
import { getTeamMembers } from '../../services/teamService'
import { getTasks } from '../../services/tasks'
import { calculateOccupancy } from '../../utils/conflictDetection'
import { getBackendUrl } from '../../utils/backendUrl'
import { ROLE_INFO, type FarmRole } from '../../types/rbac'
import { useHasPermission } from '../../hooks/useRBAC'
import type { TeamMember, UserRole as PermissionsUserRole } from '../../types/permissions'
import type { UserRole } from '../../types/user'
import type { Task } from '../../types/task'
import type { Screen } from '../../types'
import { PageHeader } from '../shared/PageHeader'
import { BottomNav, EmptyState } from '../shared'
import { EmployeeCard } from './EmployeeCard'
import { Users, Plus, RefreshCw, Clock, X } from 'lucide-react'
import toast from 'react-hot-toast'

const BASE_URL = getBackendUrl()

interface TeamUsersProps {
  onBack: () => void
  onNavigate: (screen: Screen, params?: any) => void
}

/**
 * AGC: Maps user role from user.ts to permissions.ts UserRole
 * AGC: user.ts UserRole: 'admin' | 'manager' | 'submanager' | 'user'
 * AGC: permissions.ts UserRole: 'admin' | 'manager' | 'worker'
 */
const mapUserRoleToPermissionsRole = (role: UserRole | null | undefined): PermissionsUserRole => {
  const roleMap: Record<string, PermissionsUserRole> = {
    'admin': 'admin',
    'manager': 'manager',
    'submanager': 'manager',
    'user': 'worker'
  }
  return roleMap[role || 'user'] || 'worker'
}

/**
 * AGC: TeamUsers - Team member management page
 * AGC: Features:
 * AGC: - List of all team members INCLUDING the current user (account owner)
 * AGC: - Occupancy calculation for each member
 * AGC: - Click to view member profile
 * AGC: - Add member button (owner/manager only)
 */
export const TeamUsers: React.FC<TeamUsersProps> = ({ onBack, onNavigate }) => {
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth()
  const { currentFarm } = useFarms()
  const canInvite = useHasPermission('canInviteUser')
  const [members, setMembers] = useState<TeamMember[]>([])
  const [pendingInvites, setPendingInvites] = useState<any[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [occupancyMap, setOccupancyMap] = useState<Record<string, number>>({})

  /**
   * AGC: Converts the current auth user to TeamMember format
   * AGC: This allows the account owner to appear in the team list
   */
  const convertUserToTeamMember = useCallback((): TeamMember | null => {
    if (!user?.uid) return null
    
    return {
      id: user.uid,
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || user.email || t('team.accountOwner') || 'Account Owner',
      role: mapUserRoleToPermissionsRole(user.profile?.role),
      jobTitle: t('team.accountOwner') || 'Account Owner',
      avatar: user.photoURL || undefined,
      phone: user.phoneNumber || undefined,
      farmIds: [], // AGC: Owner has access to all farms
      permissions: [], // AGC: Owner has all permissions by role
      isActive: true,
      invitedBy: user.uid, // AGC: Self-invited (account owner)
      invitedAt: user.profile?.createdAt || new Date().toISOString(),
      acceptedAt: user.profile?.createdAt || new Date().toISOString(),
      lastActive: new Date().toISOString(),
      createdAt: user.profile?.createdAt || new Date().toISOString(),
      updatedAt: user.profile?.updatedAt || new Date().toISOString(),
    }
  }, [user, t])

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const token = await getAuthToken()
      
      // AGC: Load members and tasks in parallel
      const [membersData, tasksData] = await Promise.all([
        getTeamMembers(token),
        getTasks(token),
      ])

      // AGC: Calculate occupancy for each member (current month)
      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)

      const occupancies: Record<string, number> = {}
      
      // AGC: Add current user (account owner) to the beginning of the list
      // AGC: Skip for sub-users with farmRole — they'll appear via farm-scoped API
      const hasFarmRole = !!(user?.profile as any)?.farmRole
      const currentUserMember = hasFarmRole ? null : convertUserToTeamMember()
      let allMembers = [...membersData]

      if (currentUserMember) {
        // AGC: Check if current user is not already in the list (avoid duplicates)
        const userAlreadyInList = membersData.some(
          m => m.uid === currentUserMember.uid || m.id === currentUserMember.id
        )

        if (!userAlreadyInList) {
          // AGC: Add current user at the beginning of the list
          allMembers = [currentUserMember, ...membersData]
          
          // AGC: Calculate occupancy for current user using their uid
          occupancies[currentUserMember.id] = calculateOccupancy(
            currentUserMember.uid,
            'user',
            monthStart,
            monthEnd,
            tasksData
          )
          console.log(`✅ AGC: [TEAM_USERS] Added current user to team list: ${currentUserMember.displayName} (${currentUserMember.uid})`)
        }
      }

      // AGC: Calculate occupancy for all other members
      membersData.forEach((member) => {
        occupancies[member.id] = calculateOccupancy(
          member.uid || member.id,
          'user',
          monthStart,
          monthEnd,
          tasksData
        )
      })
      
      setTasks(tasksData)

      // AGC: Load farm-scoped members + pending invites from new API
      const freshToken = await getAuthToken()
      if (currentFarm?.id && freshToken) {
        try {
          const teamRes = await fetch(`${BASE_URL}/api/v1/farms/${currentFarm.id}/team`, {
            headers: { Authorization: `Bearer ${freshToken}` },
          })
          if (teamRes.ok) {
            const teamData = await teamRes.json()
            setPendingInvites(teamData.pendingInvites || [])

            // AGC: Merge farm-scoped members into the list (avoid duplicates by uid)
            const existingUids = new Set(allMembers.map(m => m.uid || m.id))
            const farmMembers = (teamData.members || [])
              .filter((m: any) => m.status === 'active' && !existingUids.has(m.uid))
              .map((m: any) => ({
                id: m.id || m.uid,
                uid: m.uid,
                email: m.email || '',
                displayName: m.displayName || m.email || '',
                role: m.role === 'owner' ? 'admin' as PermissionsUserRole : m.role === 'manager' ? 'manager' as PermissionsUserRole : 'worker' as PermissionsUserRole,
                jobTitle: m.jobTitle || m.role || '',
                farmIds: [currentFarm.id],
                permissions: [],
                isActive: true,
                invitedBy: m.invitedBy || '',
                invitedAt: m.joinedAt || new Date().toISOString(),
                acceptedAt: m.joinedAt || new Date().toISOString(),
                createdAt: m.joinedAt || new Date().toISOString(),
                updatedAt: m.joinedAt || new Date().toISOString(),
                // AGC: Preserve original farm role for display
                _farmRole: m.role,
                _farmId: currentFarm.id,
              } as TeamMember & { _farmRole: string; _farmId: string }))

            if (farmMembers.length > 0) {
              allMembers = [...allMembers, ...farmMembers]
              // AGC: Calculate occupancy for farm-scoped members
              farmMembers.forEach((member: TeamMember) => {
                occupancies[member.id] = calculateOccupancy(member.uid, 'user', monthStart, monthEnd, tasksData)
              })
            }
          }
        } catch (err) {
          console.warn('AGC: [TEAM_USERS] Could not load farm-scoped team data:', err)
        }
      }

      // AGC: Update members with merged list
      setMembers(allMembers)
      setOccupancyMap(occupancies)

      console.log(`AGC: [TEAM_USERS] Loaded ${allMembers.length} team members (including owner)`)
    } catch (error) {
      console.error('AGC: Error loading team data:', error)
      toast.error(t('team.errors.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleMemberClick = (member: TeamMember) => {
    // AGC: Navigate to member profile page
    // AGC: For owner (current user), pass their uid as memberId
    // AGC: MemberProfile will detect this and show owner's tasks, occupancy, etc.
    const memberIdToUse = (user?.uid && (member.uid === user.uid || member.id === user.uid)) 
      ? user.uid 
      : member.id
    
    onNavigate('member-profile', { memberId: memberIdToUse })
  }

  const handleAddMember = () => {
    // AGC: Navigate to add member page
    onNavigate('team-add-member')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen app-bg">
        <PageHeader
          icon={Users}
          title={t('profile.teamManagement.title') || 'Equipe'}
        />
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen app-bg pb-20 transition-colors">
      {/* AGC: Green gradient header matching Tasks/Lots */}
      <PageHeader
        icon={Users}
        title={t('profile.teamManagement.title') || 'Equipe'}
        actions={
          <>
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title={t('common.refresh') || 'Atualizar'}
            >
              <RefreshCw className={`w-5 h-5 text-white ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            {canInvite && <button
              onClick={handleAddMember}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title={t('profile.teamManagement.addMember') || 'Adicionar'}
            >
              <Plus className="w-5 h-5 text-white" />
            </button>}
          </>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* AGC: Summary line */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {members.length} {members.length === 1 ? (t('team.member') || 'membro') : (t('team.members') || 'membros')}
          </p>
        </div>

        {/* AGC: Pending invites section */}
        {pendingInvites.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <Clock className="w-4 h-4 inline mr-1" />
              {t('team.pendingInvites') || 'Convites pendentes'} ({pendingInvites.length})
            </h3>
            {pendingInvites.map((invite: any) => {
              const roleInfo = ROLE_INFO[invite.role as FarmRole]
              return (
                <div key={invite.id} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{roleInfo?.icon || '👤'}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{invite.emailNormalized || invite.displayName}</p>
                      <p className="text-xs text-gray-500">{roleInfo?.label || invite.role}</p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const token = await getAuthToken()
                        await fetch(`${BASE_URL}/api/v1/farms/${currentFarm?.id}/team/invite/${invite.id}`, {
                          method: 'DELETE',
                          headers: { Authorization: `Bearer ${token}` },
                        })
                        setPendingInvites(prev => prev.filter(i => i.id !== invite.id))
                        toast.success(t('team.inviteRevoked') || 'Convite revogado')
                      } catch {
                        toast.error(t('team.errors.revokeFailed') || 'Erro ao revogar convite')
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    title={t('team.revokeInvite') || 'Revogar convite'}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {members.length === 0 ? (
          // AGC: Empty state
          <EmptyState
            icon={<Users />}
            title={t('profile.teamManagement.noMembers') || 'Nenhum membro'}
            description={t('profile.teamManagement.noMembersDescription') || 'Adicione seu primeiro membro da equipe'}
            action={{
              label: t('profile.teamManagement.addMember') || 'Adicionar Membro',
              onClick: handleAddMember
            }}
          />
        ) : (
          // AGC: Members grid
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map((member) => (
              <EmployeeCard
                key={member.id}
                member={member}
                occupationPercentage={occupancyMap[member.id]}
                showRole={false}
                showVerticalIndicator={false}
                onClick={() => handleMemberClick(member)}
              />
            ))}
          </div>
        )}
      </div>
      
      <BottomNav 
        active="team" 
        onNavigate={(tab) => {
          if (tab === 'dashboard') onNavigate('dashboard')
          else if (tab === 'fields') onNavigate('lotes-list')
          else if (tab === 'tasks') onNavigate('tasks')
        }} 
      />
    </div>
  )
}
